import React, { useState } from 'react';
import { useStore } from '../../store';
import { getSymbolContext } from '../../selectors/symbolContext';

export function SetupsPanel({ symbol }: { symbol: string }) {
  const { symbolData, settings, weights } = useStore();
  const data = symbolData[symbol];
  const [selected, setSelected] = useState(0);

  if (!data?.candles?.length) return (
    <div className="card">
      <div className="sl">Trade Setups</div>
      <div className="empty"><div className="icon">📋</div><h3>No Data</h3><p>Import OHLCV data to generate setups.</p></div>
    </div>
  );

  const ctx = getSymbolContext(symbol, data, settings, weights);
  const setups = ctx?.setups ?? [];

  const dirColor = (d: string) => d === 'long' ? 'var(--green)' : d === 'short' ? 'var(--red)' : 'var(--amber)';
  const confColor = (c: number) => c >= 70 ? 'var(--green)' : c >= 55 ? 'var(--amber)' : 'var(--red)';

  if (!setups.length) return (
    <div className="card">
      <div className="sl">Trade Setups</div>
      <div className="empty"><div className="icon">⏳</div><h3>No Setups</h3><p>Current conditions don't produce actionable setups. Stand aside and wait for clarity.</p></div>
      <div className="disclaimer mt8">Not financial advice. Always trade with a complete plan.</div>
    </div>
  );

  const s = setups[Math.min(selected, setups.length - 1)];

  return (
    <div className="card">
      <div className="sl">Trade Setups — {symbol} ({setups.length})</div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
        {setups.map((setup, i) => (
          <button key={i} className={`btn btn-sm ${selected === i ? 'btn-primary' : ''}`} style={{ fontSize: 11 }} onClick={() => setSelected(i)}>
            {setup.type.length > 22 ? setup.type.substring(0, 22) + '…' : setup.type}
          </button>
        ))}
      </div>

      <div className="card-inner mb12" style={{ borderLeft: `3px solid ${dirColor(s.dir)}` }}>
        <div className="fsb mb6">
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--t0)' }}>{s.type}</div>
          <span className={`badge ${s.dir === 'long' ? 'b-green' : s.dir === 'short' ? 'b-red' : 'b-amber'}`}>{s.dir}</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--t2)', lineHeight: 1.6 }}>{s.rationale}</div>
        <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 4 }}>Regime fit: {s.regimeFit}</div>
      </div>

      <div className="g2" style={{ gap: 8, marginBottom: 12 }}>
        {[
          { l: 'Entry Concept', v: s.entry, c: 'var(--blue)' },
          { l: 'Confirmation', v: s.confirmation, c: 'var(--amber)' },
          { l: 'Invalidation', v: s.invalidation, c: 'var(--red)' },
          { l: 'Targets', v: `T1: ${s.t1}  T2: ${s.t2}`, c: 'var(--green)' },
        ].map(({ l, v, c }) => (
          <div key={l} className="card-inner" style={{ borderLeft: `2px solid ${c}` }}>
            <div style={{ fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', marginBottom: 3 }}>{l}</div>
            <div style={{ fontSize: 13, color: 'var(--t0)', lineHeight: 1.5 }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="fsb">
        <span style={{ fontSize: 12, color: 'var(--t2)' }}>Setup confidence</span>
        <span className="mono" style={{ fontSize: 17, fontWeight: 700, color: confColor(s.confidence) }}>{s.confidence}%</span>
      </div>
      <div className="sbar mt4"><div className="sbar-fill" style={{ width: `${s.confidence}%`, background: confColor(s.confidence) }} /></div>

      <div className="disclaimer mt12">For educational and decision-support use only. Not investment advice. Intraday trading involves substantial risk of loss.</div>
    </div>
  );
}
